import React, { useEffect, useState } from "react";
import Button from "../../System Design/Button";
import authService from "../../services/authService";
import { Mail, Shield, LogOut, Phone, Building, Calendar, BookOpen } from "lucide-react";
import { FallingBooksAnimation } from "../Login/FallingBooksAnimation";
import { useLanguage } from "../../contexts/LanguageContext";

// Utilitaire pour construire un profil affichable à partir des données utilisateur
const buildProfileFromUser = (u) => ({
  firstName: u?.first_name || "",
  lastName: u?.last_name || "",
  email: u?.email || "",
  phone: u?.phone || u?.profile?.phone || "",
  department: u?.department || u?.profile?.department || "",
  position: u?.position || u?.profile?.position || u?.role?.name || u?.role_name || "",
  joinDate: u?.date_joined || u?.created_at || "",
  bio: u?.bio || u?.profile?.bio || "",
  avatar: u?.avatar || u?.profile?.avatar || "",
  status: u?.is_active === false ? "inactive" : "active",
});

const Profile = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(buildProfileFromUser(null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { t, language, setLanguage } = useLanguage();

  const getLocaleForLanguage = (lang) => {
    if (lang === 'en') return 'en-US';
    if (lang === 'pt') return 'pt-PT';
    return 'fr-FR';
  };

  const dateLocale = getLocaleForLanguage(language);

  useEffect(() => {
    const load = async () => {
      try {
        // 1) Afficher d'abord les données locales pour un rendu rapide
        const local = authService.getUserData();
        if (local) {
          setUser(local);
          const mapped = buildProfileFromUser(local);
          setProfile(mapped);
        }
        setLoading(false);

        // 2) Puis tenter de rafraîchir depuis l'API
        try {
          const fresh = await authService.getUserProfile();
          if (fresh) {
            setUser(fresh);
            const mappedFresh = buildProfileFromUser(fresh);
            setProfile(mappedFresh);
          }
        } catch (_) {
          // on ignore si l'API profil échoue; l'utilisateur reste affiché depuis le local
        }
      } catch (e) {
        setError(t('profile.errors.load'));

        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fullName = profile.firstName && profile.lastName
    ? `${profile.firstName} ${profile.lastName}`
    : user?.username || profile.email || t('profile.fallback.userName');

  const role = user?.role?.name || user?.role_name || t('profile.fallback.role');

  const initials = (fullName || "U")
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleLogout = async () => {
    try {
      await authService.logout();
    } finally {
      window.location.href = "/login";
    }
  };

  const joinDateDisplay = profile.joinDate
    ? new Date(profile.joinDate).toLocaleDateString(dateLocale, { year: "numeric", month: "long", day: "numeric" })
    : "—";

  return (
    <div className="flex-1 p-4 sm:p-6 md:p-8 overflow-y-auto relative overflow-hidden min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50">
      {/* Animation de fond */}
      <FallingBooksAnimation />
      {/* Décorations de fond (identiques à la page de login) */}
      <div className="absolute top-10 left-10 w-72 h-72 bg-primary-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
      <div className="absolute top-40 right-10 w-72 h-72 bg-accent-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
      <div className="absolute -bottom-8 left-20 w-72 h-72 bg-warning-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>

      <div className="relative z-10 max-w-4xl mx-auto">
        {loading ? (
          <div className="bg-white/80 backdrop-blur-lg border border-white/20 rounded-2xl p-6 sm:p-8 text-center shadow-2xl">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto mb-3"></div>
            <p className="text-gray-600">{t('profile.loading')}</p>

          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        ) : (
          <div className="bg-white/80 backdrop-blur-lg border border-white/20 rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8">
            {/* Langue de l'interface - en haut du profil */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-0">
              <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-0">
                {t('sidebar.languageLabel')}
              </p>
              <div className="flex gap-2">
                {['fr', 'en', 'pt'].map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setLanguage(code)}
                    className={`px-2 py-1 rounded-full border text-xs ${
                      language === code
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-700 border-gray-200'
                    }`}
                  >
                    {code.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary-50 text-primary flex items-center justify-center text-xl sm:text-2xl font-semibold border-4 border-primary/20">
                  {initials}
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold">{t('profile.header.title')}</h1>
                  <p className="text-sm sm:text-base text-gray-600 -mt-1">{t('profile.header.subtitle')}</p>

                </div>
              </div>
              {/* <div className="flex gap-2">
                {!isEditing ? (
                  <Button onClick={handleEdit} variant="secondary" size="md" className="border border-primary text-primary hover:bg-primary/10">
                    <Edit3 className="w-4 h-4 mr-2" /> {t('profile.buttons.edit')}
                  </Button>
                ) : (
                  <>
                    <Button onClick={handleSave} size="md" className="bg-primary hover:bg-primary/90 text-white">
                      <Save className="w-4 h-4 mr-2" /> {t('profile.buttons.save')}
                    </Button>
                    <Button onClick={handleCancel} variant="secondary" size="md" className="border border-gray-300 text-gray-600 hover:bg-gray-100">
                      <X className="w-4 h-4 mr-2" /> {t('profile.buttons.cancel')}
                    </Button>
                  </>
                )}
              </div> */}
            </div>

            {/* Sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {/* Informations personnelles */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-primary" /> {t('profile.sections.personal')}
                </h3>

                {/* Prénom / Nom */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.fields.firstName')}</label>

                    <p className="text-gray-900 bg-gray-50 p-2 rounded-md border border-gray-200">{profile.firstName || "—"}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.fields.lastName')}</label>

                    <p className="text-gray-900 bg-gray-50 p-2 rounded-md border border-gray-200">{profile.lastName || "—"}</p>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center"><Mail className="w-4 h-4 mr-2" /> {t('profile.fields.email')}</label>

                  <p className="text-gray-900 bg-gray-50 p-2 rounded-md border border-gray-200">{profile.email || "—"}</p>
                </div>

                {/* Téléphone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center"><Phone className="w-4 h-4 mr-2" /> {t('profile.fields.phone')}</label>

                  <p className="text-gray-900 bg-gray-50 p-2 rounded-md border border-gray-200">{profile.phone || "—"}</p>
                </div>
              </div>

              {/* Informations professionnelles */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <Building className="w-5 h-5 mr-2 text-primary" /> {t('profile.sections.professional')}
                </h3>

                {/* Département */}
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Département</label>
                  {isEditing ? (
                    <input
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent bg-white/70"
                      value={editedProfile.department}
                      onChange={(e) => handleInputChange("department", e.target.value)}
                    />
                  ) : (
                    <p className="text-gray-900 bg-gray-50 p-2 rounded-md border border-gray-200">{profile.department || "—"}</p>
                  )}
                </div> */}

                {/* Poste */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.fields.role')}</label>

                  <p className="text-gray-900 bg-gray-50 p-2 rounded-md border border-gray-200">{profile.position || role || "—"}</p>
                </div>

                {/* Date d'arrivée */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center"><Calendar className="w-4 h-4 mr-2" /> {t('profile.fields.joinDate')}</label>

                  <p className="text-gray-900 bg-gray-50 p-2 rounded-md border border-gray-200">{joinDateDisplay}</p>
                </div>

                {/* Statut */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.fields.status')}</label>
                  {profile.status === "active" ? (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/20">{t('profile.status.active')}</span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 border border-gray-200">{t('profile.status.inactive')}</span>
                  )}
                </div>
              </div>
            </div>

            {/* À propos */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold flex items-center">
                <BookOpen className="w-5 h-5 mr-2 text-primary" /> {t('profile.sections.about')}
              </h3>

              <div className="mt-3">
                <p className="text-gray-900 bg-gray-50 p-3 rounded-md border border-gray-200 leading-relaxed">{profile.bio || "—"}</p>
              </div>
            </div>

            {/* Actions bas de page */}
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <Button
                variant="secondary"
                className="border border-gray-300 text-gray-700 hover:bg-gray-100"
                onClick={() => window.history.back()}
              >
                {t('profile.buttons.back')}
              </Button>
              <Button
                variant="primary"
                className="bg-red-600 hover:bg-red-600/90"
                onClick={handleLogout}
              >
                <LogOut size={18} className="mr-2" />
                {t('profile.buttons.logout')}
              </Button>

            </div>

            {/* Footer */}
            <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-white/40 text-center text-xs text-gray-500">
              {new Date().getFullYear()} {t('profile.footer')}
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;